import { cn } from "@/common/helpers/utils";
import { useDevice } from "@/common/hooks/use-device";
import { usePaletteCommands } from "@/components/command-palette/use-palette-commands";
import type { PaletteCommand } from "@/components/command-palette/types";
import { usePermittedItems } from "@/components/detail-page/_hooks/use-permitted-items";
import type { DetailAction } from "@/components/detail-page/types";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EllipsisVerticalIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

interface DetailActionsProps {
  actions?: DetailAction[];
  className?: string;
}

export function DetailActions({ actions = [], className }: DetailActionsProps): ReactElement | null {
  const { isMobile } = useDevice();
  const permittedActions = usePermittedItems(actions);

  usePaletteCommands(
    permittedActions
      .filter((action) => !action.disabled)
      .map((action): PaletteCommand => ({
        id: action.id,
        title: action.label,
        icon: action.icon && <action.icon />,
        perform: action.onClick,
      })),
  );

  const MAX_INLINE_ACTIONS = 3;
  const MAX_INLINE_ACTIONS_MOBILE = 0;

  if (permittedActions.length === 0) {
    return null;
  }

  const inlineLimit = isMobile ? MAX_INLINE_ACTIONS_MOBILE : MAX_INLINE_ACTIONS;
  const { inlineActions, menuActions } = splitActionsByPriority(permittedActions, inlineLimit);

  return (
    <div className={cn("flex shrink-0 items-center gap-2", className)}>
      {inlineActions.map((action) => (
        <InlineActionButton key={action.id} action={action} />
      ))}
      {menuActions.length > 0 && <ActionsOverflowMenu actions={menuActions} />}
    </div>
  );
}

interface SplitActions {
  inlineActions: DetailAction[];
  menuActions: DetailAction[];
}

function splitActionsByPriority(actions: DetailAction[], inlineLimit: number): SplitActions {
  const inlineActions: DetailAction[] = [];
  const menuActions: DetailAction[] = [];
  for (const action of actions) {
    if (!action.menuOnly && inlineActions.length < inlineLimit) {
      inlineActions.push(action);
    } else {
      menuActions.push(action);
    }
  }
  return { inlineActions, menuActions };
}

function InlineActionButton({ action }: { action: DetailAction }): ReactElement {
  const button = (
    <Button
      variant={ButtonVariant.Outlined}
      colorVariant={action.destructive ? ButtonColorVariant.Error : ButtonColorVariant.Secondary}
      size={ButtonSize.Sm}
      onClick={action.onClick}
      disabled={action.disabled}
    >
      {action.icon && <action.icon />}
      {action.label}
    </Button>
  );

  if (!action.disabled || !action.disabledReason) return button;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-not-allowed" tabIndex={0} aria-disabled="true">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>{action.disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ActionsOverflowMenu({ actions }: { actions: DetailAction[] }): ReactElement {
  const { t } = useTranslation("common");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          size={ButtonSize.Icon}
          aria-label={t("common:MoreActions")}
        >
          <EllipsisVerticalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.id}
            className={cn(action.destructive && "text-error focus:text-error")}
            onSelect={action.onClick}
            disabled={action.disabled}
            aria-label={action.label}
          >
            {action.icon && <action.icon />}
            <span>
              {action.label}
              {action.disabled && action.disabledReason ? (
                <span className="block text-xs font-normal">{action.disabledReason}</span>
              ) : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
